import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from 'aws-amplify/api';
import { format } from 'date-fns';

import { Box, Fade, IconButton, Modal, Stack, Tooltip, Typography } from '@mui/material';
import { Close, ContentCopy, DeleteRounded } from '@mui/icons-material';

import { PopUp } from 'components/PopUp';

import * as SourceService from 'services/sourceService';

import { useToaster } from 'contexts/ToasterContext';

import { FAQS_IMAGE_DISTRIBUTION } from 'constants/CommonConstants';

import { PopUpButtonProps } from 'types';
import { getAPIErrorMessage } from 'utils/helpers';

import { FAQsImage } from './ImagesDashboard';
import { useImages } from '../../../contexts/ImagesContext';

interface EnlargedImageProps {
	imagePath: string;
	isOpen: boolean;
	onClose: () => void;
	altText?: string;
}

export const ImageCard = ({ image }: { image: FAQsImage }) => {
	const [isDeleteImagePopUpOpen, setIsDeleteImagePopUpOpen] = useState(false);
	const [isImageEnlarged, setIsImageEnlarged] = useState(false);
	const { triggerToast } = useToaster();
	const { refetchImages } = useImages();
	const { mutate: deleteImage, isPending: isDeleting } = useMutation({
		mutationFn: SourceService.deleteFaqsImages,
		onError(error: ApiError) {
			const defaultMessage = 'Failed to delete the image!';
			const errMessage = getAPIErrorMessage(error) || defaultMessage;
			const statusCode = error?.response?.statusCode || 500;
			triggerToast({
				message: statusCode === 500 ? defaultMessage : errMessage,
				type: 'error',
			});
			setIsDeleteImagePopUpOpen(false);
		},
		onSuccess() {
			// Use our refetchImages function from context instead of directly invalidating the query
			refetchImages();
			triggerToast({
				type: 'success',
				message: 'Image deleted! This image will vanish shortly',
			});
			setIsDeleteImagePopUpOpen(false);
		},
	});

	const buttons: PopUpButtonProps[] = [
		{ name: 'CANCEL', handler: () => setIsDeleteImagePopUpOpen(false), variant: 'outlined' },
		{
			name: 'DELETE',
			handler: () => {
				deleteImage(image.path);
			},
			isLoading: isDeleting,
			variant: 'contained',
		},
	];

	return (
		<Stack
			p={2}
			borderRadius={1}
			width="100%"
			height="300px"
			border="1px solid"
			borderColor="highlight.secondary"
			overflow="hidden"
			direction="column"
			alignItems="flex-start"
			sx={{
				'&:hover': {
					borderColor: 'grey.300',
				},
			}}
		>
			<Stack direction="row" justifyContent="space-between" mb={1} width="100%">
				<Box>
					<Typography variant="body2">
						<b>
							{(image?.originalFileName.includes('.') ? image.originalFileName.split('.')[0] : image?.originalFileName)
								?.trim()
								.substring(0, 32)}
							{image.originalFileName.length > 32 ? '...' : ''}
						</b>
					</Typography>
					<p style={{ fontSize: '12px', margin: 0, padding: 0 }}>
						{format(new Date(image?.lastModified), 'MMM dd  hh:mm a')}
					</p>
				</Box>
				<Stack direction="row" spacing={1}>
					<Tooltip title="Copy image link">
						<IconButton
							size="small"
							color="primary"
							onClick={() => {
								navigator.clipboard
									.writeText(FAQS_IMAGE_DISTRIBUTION + image?.path)
									.then(() => {
										triggerToast({
											type: 'success',
											message: 'Image link copied to clipboard!',
										});
									})
									.catch((err) => {
										console.error('Failed to copy: ', err);
										triggerToast({
											type: 'error',
											message: 'Failed to copy image link',
										});
									});
							}}
						>
							<ContentCopy fontSize="small" />
						</IconButton>
					</Tooltip>
					{true && (
						<Tooltip title="Delete image">
							<IconButton size="small" color="error" onClick={() => setIsDeleteImagePopUpOpen(true)}>
								<DeleteRounded />
							</IconButton>
						</Tooltip>
					)}
				</Stack>
			</Stack>
			<Box width="100%" maxHeight="calc(100% - 52px)" borderRadius={1} flexGrow={1} position="relative">
				<img
					src={FAQS_IMAGE_DISTRIBUTION + image.path}
					alt={'Image'}
					loading="lazy"
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'contain',
						cursor: 'pointer',
					}}
					onClick={() => setIsImageEnlarged(true)}
				/>

				<EnlargedImage
					imagePath={FAQS_IMAGE_DISTRIBUTION + image.path}
					isOpen={isImageEnlarged}
					onClose={() => setIsImageEnlarged(false)}
					altText="Image"
				/>
			</Box>
			<PopUp
				isPopUpOpen={isDeleteImagePopUpOpen}
				buttons={buttons}
				close={() => setIsDeleteImagePopUpOpen(false)}
				description={
					<Typography sx={{ fontWeight: '600' }}>
						Are you sure you want to
						<Typography
							component="span"
							variant="body1"
							sx={{
								color: 'primary.main',
								fontWeight: '600',
								mx: '5px',
							}}
						>
							delete
						</Typography>
						the image &ldquo;{image.originalFileName}&rdquo;?
					</Typography>
				}
			></PopUp>
		</Stack>
	);
};

export const EnlargedImage: React.FC<EnlargedImageProps> = ({ imagePath, isOpen, onClose, altText = 'Image' }) => {
	return (
		<Modal
			open={isOpen}
			onClose={onClose}
			closeAfterTransition
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<Fade in={isOpen}>
				<Box
					sx={{
						position: 'relative',
						width: '75%',
						height: '75%',
						maxWidth: '1200px',
						maxHeight: '800px',
						outline: 'none',
						p: 1,
						bgcolor: 'background.paper',
						borderRadius: 1,
						boxShadow: 24,
						overflow: 'hidden',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<IconButton
						sx={{
							position: 'absolute',
							top: 8,
							right: 8,
							bgcolor: 'rgba(0, 0, 0, 0.5)',
							color: 'white',
							'&:hover': {
								bgcolor: 'rgba(0, 0, 0, 0.7)',
							},
							zIndex: 1,
						}}
						onClick={onClose}
					>
						<Close />
					</IconButton>
					<img
						src={imagePath}
						alt={altText}
						style={{
							maxWidth: '90%',
							maxHeight: '90%',
							objectFit: 'contain',
							display: 'block',
						}}
					/>
				</Box>
			</Fade>
		</Modal>
	);
};
